'use client';

import type { ConditionalValue, SystemStyleObject } from '@chakra-ui/react';
import React from 'react';

import { Box, BoxProps } from './box';

/**
 * Grid component props with full responsive value support.
 *
 * All grid-related props accept responsive objects like:
 * ```tsx
 * <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}>
 * ```
 */
export interface GridProps extends BoxProps {
  /** Grid template columns - supports responsive values */
  templateColumns?: SystemStyleObject['gridTemplateColumns'];
  /** Grid template rows - supports responsive values */
  templateRows?: SystemStyleObject['gridTemplateRows'];
  /** Gap between grid items - supports responsive values */
  gap?: SystemStyleObject['gap'];
  /** Row gap - supports responsive values */
  rowGap?: SystemStyleObject['rowGap'];
  /** Column gap - supports responsive values */
  columnGap?: SystemStyleObject['columnGap'];
  /** Grid auto flow - supports responsive values */
  autoFlow?: SystemStyleObject['gridAutoFlow'];
  /** Grid auto columns - supports responsive values */
  autoColumns?: SystemStyleObject['gridAutoColumns'];
  /** Grid auto rows - supports responsive values */
  autoRows?: SystemStyleObject['gridAutoRows'];
  /** Grid template areas - supports responsive values */
  templateAreas?: SystemStyleObject['gridTemplateAreas'];
  /** Justify items - supports responsive values */
  justifyItems?: SystemStyleObject['justifyItems'];
  /** Align items - supports responsive values */
  alignItems?: SystemStyleObject['alignItems'];
  /** Place items shorthand - supports responsive values */
  placeItems?: SystemStyleObject['placeItems'];
  /** Justify content - supports responsive values */
  justifyContent?: SystemStyleObject['justifyContent'];
  /** Align content - supports responsive values */
  alignContent?: SystemStyleObject['alignContent'];
  /** Place content shorthand - supports responsive values */
  placeContent?: SystemStyleObject['placeContent'];
}

export const Grid = React.forwardRef<HTMLDivElement, GridProps>(
  (
    {
      templateColumns,
      templateRows,
      gap,
      rowGap,
      columnGap,
      autoFlow,
      autoColumns,
      autoRows,
      templateAreas,
      justifyItems,
      alignItems,
      placeItems,
      justifyContent,
      alignContent,
      placeContent,
      ...props
    },
    ref
  ) => {
    return (
      <Box
        ref={ref}
        display="grid"
        gridTemplateColumns={templateColumns}
        gridTemplateRows={templateRows}
        gap={gap}
        rowGap={rowGap}
        columnGap={columnGap}
        gridAutoFlow={autoFlow}
        gridAutoColumns={autoColumns}
        gridAutoRows={autoRows}
        gridTemplateAreas={templateAreas}
        justifyItems={justifyItems}
        alignItems={alignItems}
        placeItems={placeItems}
        justifyContent={justifyContent}
        alignContent={alignContent}
        placeContent={placeContent}
        {...props}
      />
    );
  }
);

Grid.displayName = 'Grid';

/**
 * GridItem component props with full responsive value support.
 *
 * All grid item props accept responsive objects like:
 * ```tsx
 * <GridItem colSpan={{ base: 1, md: 2 }}>
 * ```
 */
export interface GridItemProps extends BoxProps {
  /** Number of columns to span - supports responsive values */
  colSpan?: ConditionalValue<number | 'auto'>;
  /** Number of rows to span - supports responsive values */
  rowSpan?: ConditionalValue<number | 'auto'>;
  /** Grid column start position - supports responsive values */
  colStart?: ConditionalValue<number | 'auto'>;
  /** Grid column end position - supports responsive values */
  colEnd?: ConditionalValue<number | 'auto'>;
  /** Grid row start position - supports responsive values */
  rowStart?: ConditionalValue<number | 'auto'>;
  /** Grid row end position - supports responsive values */
  rowEnd?: ConditionalValue<number | 'auto'>;
  /** Grid area name - supports responsive values */
  area?: SystemStyleObject['gridArea'];
}

/**
 * Transform span value to CSS grid span syntax.
 * Handles responsive objects by mapping each value.
 *
 * @example
 * spanToGrid(2) // "span 2 / span 2"
 * spanToGrid({ base: 1, md: 2 }) // { base: "span 1 / span 1", md: "span 2 / span 2" }
 */
function spanToGrid(
  span: ConditionalValue<number | 'auto'> | undefined
): ConditionalValue<string> | undefined {
  if (span === undefined) return undefined;

  // Handle primitive values (number or "auto")
  if (typeof span === 'number' || span === 'auto') {
    return span === 'auto' ? 'auto' : `span ${span} / span ${span}`;
  }

  // Handle responsive object: { base: 1, md: 2, lg: 3 }
  if (typeof span === 'object' && span !== null) {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(span)) {
      if (value !== undefined) {
        result[key] = value === 'auto' ? 'auto' : `span ${value} / span ${value}`;
      }
    }
    return result as ConditionalValue<string>;
  }

  return undefined;
}

export const GridItem = React.forwardRef<HTMLDivElement, GridItemProps>(
  ({ colSpan, rowSpan, colStart, colEnd, rowStart, rowEnd, area, ...props }, ref) => {
    // Transform span values to CSS grid syntax (handles responsive objects)
    const gridColumn = React.useMemo(() => spanToGrid(colSpan), [colSpan]);
    const gridRow = React.useMemo(() => spanToGrid(rowSpan), [rowSpan]);

    return (
      <Box
        ref={ref}
        gridColumn={gridColumn}
        gridRow={gridRow}
        gridColumnStart={colStart}
        gridColumnEnd={colEnd}
        gridRowStart={rowStart}
        gridRowEnd={rowEnd}
        gridArea={area}
        {...props}
      />
    );
  }
);

GridItem.displayName = 'GridItem';
